import { useEffect, useState } from "react";
import { Download, X, Smartphone } from "lucide-react";

/**
 * PWAInstallNudge — one-time auto-prompted modal that asks the user to add
 * Grubpe to their home screen. Trigger keys map to user moments:
 *   - "login"        → fired right after successful login
 *   - "first-order"  → fired after the user's first QR order success
 * Each trigger persists a localStorage flag so we never nag the same user twice
 * for the same trigger. Already-installed users (display-mode standalone) are
 * skipped.
 */
export default function PWAInstallNudge({ trigger }) {
  const [open, setOpen] = useState(false);
  const [deferred, setDeferred] = useState(null);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (!trigger) return;
    const ua = window.navigator.userAgent || "";
    const isStandalone = window.matchMedia?.("(display-mode: standalone)").matches || window.navigator.standalone;
    if (isStandalone) return;

    const seen = (() => {
      try { return localStorage.getItem(`grubpe_install_nudge_${trigger}`) === "1"; } catch { return false; }
    })();
    const dismissedAll = (() => {
      try { return localStorage.getItem("grubpe_install_dismissed") === "1"; } catch { return false; }
    })();
    if (seen || dismissedAll) return;

    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);

    const onPrompt = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Defer the modal slightly so it doesn't compete with toasts/redirects
      setTimeout(() => setOpen(true), 800);
    };
    window.addEventListener("beforeinstallprompt", onPrompt);

    if (isIOS && isSafari) {
      // iOS never fires beforeinstallprompt; show hint anyway.
      setIosHint(true);
      setTimeout(() => setOpen(true), 800);
    }

    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, [trigger]);

  const remember = () => {
    try { localStorage.setItem(`grubpe_install_nudge_${trigger}`, "1"); } catch {}
  };

  const dismiss = () => {
    remember();
    setOpen(false);
  };

  const install = async () => {
    remember();
    if (!deferred) { setOpen(false); return; }
    deferred.prompt();
    try { await deferred.userChoice; } catch {}
    setDeferred(null);
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-ink/40 z-[60] flex items-end sm:items-center justify-center px-4 pb-4 sm:pb-0" data-testid={`pwa-nudge-${trigger}`}>
      <div className="bg-white rounded-3xl p-6 w-full max-w-sm shadow-floating">
        <div className="flex items-start gap-3">
          <div className="w-12 h-12 rounded-2xl bg-brand text-white flex items-center justify-center flex-shrink-0">
            <Smartphone size={22} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-heading text-xl font-extrabold text-ink">Install Grubpe?</div>
            <div className="text-sm text-ink-600 mt-1">
              {iosHint
                ? "Tap Share → Add to Home Screen for one-tap ordering, faster checkout, and offline menu."
                : "One-tap ordering, faster checkout, and you'll never lose the link in browser tabs again."}
            </div>
          </div>
          <button onClick={dismiss} data-testid="pwa-nudge-close" className="text-ink-400 hover:text-ink"><X size={18}/></button>
        </div>
        <div className="mt-5 flex flex-col gap-2">
          {!iosHint && (
            <button data-testid="pwa-nudge-install" onClick={install}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-brand text-white font-bold text-sm">
              <Download size={14}/> Install app
            </button>
          )}
          <button data-testid="pwa-nudge-later" onClick={dismiss}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full text-ink-700 font-semibold text-sm hover:bg-cream-200">
            {iosHint ? "Got it" : "Maybe later"}
          </button>
        </div>
      </div>
    </div>
  );
}
